/**
 * Dependency imports
 */
import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Box, BoxProps, Image, Stack, Text, Title } from '@mantine/core';
import Link from 'next/link';
import TransitionLink from './TransitionLink';

/**
 * Local imports
 */

/**
 * Root
 */
const Root = styled(Box, {
  shouldForwardProp: (prop: PropertyKey) => ![''].includes(prop.toString()),
})<Partial<CardCombinedProps>>(
  (props) => css`
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      z-index: -1;
      background: transparent;
      transition: 200ms background-color ease-out, 200ms top ease-out,
        200ms left ease-out, 200ms right ease-out, 200ms bottom ease-out;
    }

    figure {
      overflow: hidden;
      background-color: #f6f6f6;

      img {
        transition: 200ms transform ease-out, 200ms opacity ease-out;
      }
    }

    &:hover {
      :before {
        top: -25px;
        left: -25px;
        right: -25px;
        bottom: -25px;
        background: #f6f6f6;
      }

      figure {
        img {
          transform: scale(1.035);
        }
      }
    }

    > a {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 1;
      font-size: 0;
      color: transparent;
    }
  `
);

/**
 * Card Component
 */
export interface CardProps {
  title: string;
  content: React.ReactNode;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  date?: string;
  href?: string;
}

export type CardCombinedProps = CardProps & BoxProps;

const Card: React.FC<CardCombinedProps> = ({
  title,
  content,
  image,
  imageWidth,
  imageHeight,
  date,
  href,
  ...props
}) => {
  const [imageLoaded, setImageLoaded] = React.useState(false);

  return (
    <Root {...props}>
      {href && <TransitionLink href={href} aria-label={title}>{title}</TransitionLink>}

      <Stack gap={20}>
        <figure>
          <Image
            src={image}
            alt={title}
            style={{
              aspectRatio: `${imageWidth}/${imageHeight}`,
              opacity: imageLoaded ? 1 : 0,
            }}
            onLoad={() => setImageLoaded(true)}
          />
        </figure>

        <Stack gap={5}>
          {date && (
            <Text c="dark.2" fz={16}>
              {date}
            </Text>
          )}
          <Title size={21} fw={500}>
            {title}
          </Title>
        </Stack>

        <Text c="dark.3" lineClamp={3}>
          {content}
        </Text>
      </Stack>
    </Root>
  );
};

export default Card;
